import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { change, FieldArray, Field } from "redux-form";
import { InputWithIcon, Radio, DateAndTimePicker } from "../../../form/";
import {
  FormTemplateField,
  FormTemplateFields,
  FormTemplateHeader,
  FormTemplateSectionWrapper,
} from "../../../others";
import { dateTimeFormat } from "../../../HelperFunctions";
import LineGraph from "../../../../../containers/authorised/addOns/other/graphs/LineGraph";
import Tooltip from "../../../others/Tooltip";
import ExamSingleNote from "../ExamSingleNote";
import { examUpdate } from "../../../../../redux/actions/exam/examActions";

const FORM = "examExternalInternalForm";
const DEFAULT_FIELD = (label) => ({
  visible: "true",
  is_required: "false",
  custom_label: label,
  default_label: label,
});

const chartOptions = {
  legend: { display: true },
  tooltips: {
    callbacks: {
      label: (tooltipItem, data) =>
        data.datasets[tooltipItem.datasetIndex].timestamps[tooltipItem.index],
    },
  },
  scales: {
    xAxes: [{ scaleLabel: { display: true, labelString: "Reading" } }],
    yAxes: [{
      scaleLabel: { display: true, labelString: "Pressure (mmHg)" },
      ticks: { maxTicksLimit: 10, beginAtZero: false, stepSize: 3 },
    }],
  },
};

function buildGraphData(readings, filterMethod) {
  const filtered = filterMethod === "All Methods"
    ? readings
    : readings.filter((r) => r.method_name === filterMethod);
  const reversed = [...filtered].reverse();
  const series = (key, color) => ({
    label: key.toUpperCase(),
    data: reversed.map((r) => (r[key] !== "" && r[key] != null ? Number(r[key]) : null)),
    timestamps: reversed.map((r) => r.performed_at),
    borderColor: color,
    backgroundColor: "transparent",
    pointBackgroundColor: color,
    pointRadius: 4,
    tension: 0.3,
    fill: false,
  });
  return {
    customLabelData: reversed.map((_, i) => String(i + 1)),
    customDataSets: [series("od", "#4caf50"), series("os", "#2196f3")],
  };
}

// const isNumeric = (v) => v !== "" && v != null && !isNaN(Number(v));
const isNumeric = (value) => {
  return /^\d+(\.\d+)?$/.test(String(value).trim());
};

const RenderTonometries = ({ fields, isTemplate, template, pprops, storeTimeZone, formValues, patientTonometries }) => {
  const fieldDefs = template?.external_internal_exam?.tonometry?.fields;
  const methodFieldDef = fieldDefs?.type;
  const values = formValues || {};
  const selectedMethod = values.type || "";
  const [otherMethodName, setOtherMethodName] = useState("");

  // Default method comes from examExternalInternalForm initialValues.
  // Do not dispatch change() here — it marks the whole exam form dirty and triggers autosave.

  const methodOptions = Object.entries(fieldDefs || {})
    .filter(([, f]) => f.type === "method_name")
    .map(([key, f]) => ({
      value: f.default_label || key,
      label: f.custom_label || f.default_label || key,
      optBaseName: `external_internal_exam[tonometry][fields][${key}]`,
      visible: f.visible,
      required: f.required,
    }));

  const visibleMethods = methodOptions.filter((o) => o.visible === true || o.visible === "true");
  const isAddDisabled =
    !selectedMethod ||
    (selectedMethod === "Other" && !otherMethodName.trim()) ||
    (!String(values.od || "").trim() && !String(values.os || "").trim());

  const getPerformedAt = () => {
    const performedAt = values.performed_at;
    if (!performedAt) return dateTimeFormat(new Date(), storeTimeZone);
    if (performedAt instanceof Date) return dateTimeFormat(performedAt, storeTimeZone);
    const trimmed = String(performedAt).trim();
    if (!trimmed) return dateTimeFormat(new Date(), storeTimeZone);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const time = dateTimeFormat(new Date(), storeTimeZone).split(" ").slice(1).join(" ");
      return `${trimmed} ${time}`;
    }
    return trimmed;
  };

  const clearAndSubmit = () => {
    ["od", "os", "performed_at"].forEach((f) => pprops.dispatch(change(FORM, f, "")));
    setTimeout(() => pprops.submit(), 500);
  };

  const handleAddNew = () => {
    fields.push({
      patient_id: pprops.exam?.patient_id || values.patient_id,
      od: values.od || "",
      os: values.os || "",
      performed_at: getPerformedAt(),
      method_name: selectedMethod === "Other" ? (otherMethodName.trim() || "Other") : selectedMethod,
    });
    clearAndSubmit();
  };

  return (
    <div className={isTemplate ? "col-12" : "tonometry-panel"}>
      {!isTemplate && <h6 className="mb-4 font-weight-bold small text-uppercase">Methods</h6>}
      {!isTemplate && (
        <div className="d-flex flex-wrap align-items-center mb-5">
          {visibleMethods.map((option) => (
            <Field
              key={option.value}
              name="type"
              component={Radio}
              type="radio"
              value={option.value}
              label={option.label}
              required={option.required}
              wrapperClasses="mr-4 mb-2 d-inline-block"
            />
          ))}
          {selectedMethod === "Other" && (
            <div className="mb-2">
              <input
                type="text"
                className={`form-control form-control-sm ${!otherMethodName.trim() ? "is-invalid" : ""}`}
                placeholder="Enter method name"
                value={otherMethodName}
                onChange={(e) => setOtherMethodName(e.target.value)}
                style={{ maxWidth: 140 }}
              />
              {!otherMethodName.trim() && (
                <div className="invalid-feedback d-block">Method name is required.</div>
              )}
            </div>
          )}
        </div>
      )}
      {isTemplate && (
        <div className="my-8 label-icons">
          <FormTemplateFields
            isVisible={methodFieldDef?.visible !== false && methodFieldDef?.visible !== "false"}
            baseName="external_internal_exam[tonometry][fields][type]"
            isRequired={methodFieldDef?.required}
            radioOptions={methodOptions}
            hideIsRequired={false}
            form="examTemplateForm"
          />
        </div>
      )}

      <div className="row">
        {["od", "os"].map((eye) => (
          <FormTemplateField
            key={eye}
            isTemplate={isTemplate}
            name={eye}
            baseName={`external_internal_exam[tonometry][fields][${eye}]`}
            fieldValues={fieldDefs?.[eye] || DEFAULT_FIELD(eye.toUpperCase())}
            component={InputWithIcon}
            title={eye.toUpperCase()}
            titleAppend="mmHG"
            className="form-control"
            wrapperClasses="col-6 mb-5 label-icons form-group"
          />
        ))}
      </div>

      <div className="row align-items-end">
        <div className="col-12 col-xl-6 mb-4">
          <FormTemplateField
            isTemplate={isTemplate}
            name="performed_at"
            baseName="external_internal_exam[tonometry][fields][time]"
            fieldValues={fieldDefs?.time || DEFAULT_FIELD("Time")}
            component={DateAndTimePicker}
            wrapperClasses="label-icons mb-0"
            timeSlotDuration={1}
            disableKeyboardInput
          />
        </div>
        {!isTemplate && (
          <div className="col-12 col-xl-6 mb-4 d-flex align-items-center">
            <button
              type="button"
              className="btn btn-primary mr-3"
              onClick={() =>
                pprops.dispatch(change(FORM, "performed_at", new Date()))
              }
            >
              Date/Time
            </button>
            {/* <Tooltip tooltipContent={isAddDisabled ? "OD and OS Values must be Numeric" : ""}> */}
              <button
                type="button"
                disabled={isAddDisabled}
                className="btn text-nowrap btn-outline-primary"
                onClick={handleAddNew}
              >
                <i className="la la-plus" /> Add
              </button>
            {/* </Tooltip> */}
          </div>
        )}
      </div>
      {!isTemplate && (
        <ExamSingleNote wrapperClasses="mt-2 px-0" noteType="tonometry" tonometryNote />
      )}
    </div>
  );
};

export function TonometryTable({ readings, handleDelete, isOverview }) {
  return (
    <div className={isOverview ? "" : "tonometry-table-scroll"}>
      <table className="table table-sm table-bordered table-hover mb-0">
        <thead className="thead-light">
          <tr>
            <th>#</th>
            <th>Date / Time</th>
            <th>Method</th>
            <th>OD</th>
            <th>OS</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {readings.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted py-3">No readings recorded</td>
            </tr>
          ) : (
            readings.map((r, i) => (
              <tr key={r.id || `new-${i}`}>
                <td>{i + 1}</td>
                <td className="text-nowrap small">{r.performed_at}</td>
                <td>{r.method_name}</td>
                <td>{r.od}</td>
                <td>{r.os}</td>
                <td className="text-center">
                  {handleDelete && (
                    <i
                      className="la la-trash la-sm text-danger pointer"
                      onClick={() => handleDelete(r)}
                    />
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="d-flex align-items-center mr-3">
      <span style={{ display: "inline-block", width: 10, height: 10, background: color, borderRadius: "50%", marginRight: 4 }} />
      <small>{label}</small>
    </span>
  );
}

function TonometryTableAndGraph({ formValues, patientTonometries, handleDelete }) {
  const [graphFilter, setGraphFilter] = useState("All Methods");
  const formReadings = formValues?.tonometries_attributes || [];
  const readings = [
    ...formReadings.filter((r) => !r.id && !r._destroy),
    ...(patientTonometries || []),
  ];

  const { customLabelData, customDataSets } = useMemo(
    () => buildGraphData(readings, graphFilter),
    [readings, graphFilter],
  );
  const methods = [...new Set(readings.map((r) => r.method_name).filter(Boolean))];

  return (
    <>
      <div className="tonometry-panel pt-3">
        <h6 className="mb-6 font-weight-bold small text-uppercase">All Readings</h6>
        <TonometryTable readings={readings} handleDelete={handleDelete} />
      </div>
      <div className="tonometry-panel">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h6 className="mb-2 font-weight-bold small text-uppercase">Pressure Trend</h6>
          <div className="d-flex flex-wrap align-items-center mb-2">
            <select
              className="form-control form-control-sm mr-3"
              style={{ width: 130 }}
              value={graphFilter}
              onChange={(e) => setGraphFilter(e.target.value)}
            >
              <option value="All Methods">All Methods</option>
              {methods.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <LegendDot color="#4caf50" label="OD" />
            <LegendDot color="#2196f3" label="OS" />
          </div>
        </div>
        <LineGraph
          showLegend={false}
          customLabelData={customLabelData}
          customDataSets={customDataSets}
          customOptions={chartOptions}
        />
      </div>
    </>
  );
}

const ConnectedTableAndGraph = connect((state) => ({
  formValues: state.form?.examExternalInternalForm?.values || {},
  patientTonometries: state.exam?.exam?.patient_tonometries_attributes || [],
}))(TonometryTableAndGraph);

function TonometryDisplay({ fieldsValues }) {
  const readings = (fieldsValues || []).filter((r) => r.is_marked_complete);
  if (!readings.length) return null;
  return (
    <div>
      {readings.map((r, i) => (
        <div key={i} className="mb-3">
          {r.method_name && <div><strong>Method:</strong> {r.method_name}</div>}
          {r.od && <div><strong>OD:</strong> {r.od} mmHg</div>}
          {r.os && <div><strong>OS:</strong> {r.os} mmHg</div>}
          {r.performed_at && <div><strong>Time:</strong> {r.performed_at}</div>}
        </div>
      ))}
    </div>
  );
}

const TonometryComponent = ({ template, isTemplate, pprops, storeTimeZone, fieldsValues, formValues, patientTonometries }) => {
  if (fieldsValues !== undefined) {
    return <TonometryDisplay fieldsValues={fieldsValues} />;
  }

  const handleDelete = (reading) => {
    if (!reading?.id) {
      const current = formValues?.tonometries_attributes || [];
      const idx = current.findIndex(
        (r) =>
          !r.id &&
          r.od === reading.od &&
          r.os === reading.os &&
          r.performed_at === reading.performed_at &&
          r.method_name === reading.method_name,
      );
      if (idx >= 0) {
        pprops.dispatch(
          change(FORM, "tonometries_attributes", current.filter((_, i) => i !== idx)),
        );
      }
      return;
    }

    pprops.dispatch(
      examUpdate({ id: pprops.exam.id, destroy_tonometry: reading.id }, { include: "*" }),
    );
  };

  const tonoTemplate = template?.external_internal_exam?.tonometry || {
    visible: "true",
    custom_label: "Tonometry",
    default_label: "Tonometry",
  };

  return (
    <FormTemplateSectionWrapper isTemplate={isTemplate} fieldValues={tonoTemplate}>
      <div className="card border-0 shadow-sm px-8 mt-7 w-100">
        <div className="py-3 border-bottom border-light">
          <FormTemplateHeader
            isTemplate={isTemplate}
            fieldValues={tonoTemplate}
            baseName="external_internal_exam[tonometry]"
            overviewName="tonometry"
          />
        </div>
        <div className={`card-body pt-5 ${isTemplate ? "row" : "tonometry-grid"}`}>
          <FieldArray
            name={isTemplate ? "external_internal_exam[tonometry][tonometries]" : "tonometries_attributes"}
            component={RenderTonometries}
            props={{ template, isTemplate, pprops, storeTimeZone, formValues, patientTonometries }}
          />
          {!isTemplate && <ConnectedTableAndGraph handleDelete={handleDelete} />}
        </div>
      </div>
    </FormTemplateSectionWrapper>
  );
};

export default connect((state) => ({
  storeTimeZone: state.currentStore?.store?.timezone,
  formValues: state.form?.examExternalInternalForm?.values || {},
  patientTonometries: state.exam?.exam?.patient_tonometries_attributes || [],
}))(TonometryComponent);
